import React, { useState, useEffect, useRef } from 'react';
import { 
  Grid, 
  Box, 
  MenuItem,
  Select,
  SelectChangeEvent,
  InputLabel,
  FormControl,
  FormHelperText,
  Typography,
  Card,
  CardContent
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  Numbers as NumbersIcon
} from '@mui/icons-material';
import TimerDisplay from './TimerDisplay';
import HookFormTextField from './HookFormTextField';
import { useForm, Controller } from 'react-hook-form';
import { TextField } from '@mui/material';

// Define props for the form header
interface FormHeaderProps {
  dealNumber: string;
  dealType: string;
  closingDate: string;
  cof: string;
  bfsStage: string;
  startTime: Date | null;
  submitError: boolean;
  setFormState: (updater: (prevState: any) => any) => void;
  handleTimeUpdate: (seconds: number) => void;
  handleDealTypeChange: (e: SelectChangeEvent) => void;
  handleBfsStageChange: (e: SelectChangeEvent) => void;
}

// Create the FormHeader component
const FormHeader: React.FC<FormHeaderProps> = ({
  dealNumber,
  dealType,
  closingDate,
  cof,
  bfsStage,
  startTime,
  submitError,
  setFormState,
  handleTimeUpdate,
  handleDealTypeChange,
  handleBfsStageChange
}) => {
  // Create a single React Hook Form instance for the header fields
  const methods = useForm({
    defaultValues: {
      dealNumber: dealNumber || '',
      closingDate: closingDate || '',
      cof: cof || ''
    }
  });
  
  const { control, setValue } = methods;
  
  // Keep form values in sync with parent state
  useEffect(() => {
    setValue('dealNumber', dealNumber || '');
    setValue('closingDate', closingDate || '');
    setValue('cof', cof || '');
  }, [dealNumber, closingDate, cof, setValue]);
  
  // Track if we've tried to start the timer
  const timerAttemptedRef = useRef(false);
  
  // Function to start the timer
  const startTimer = () => {
    // Only start the timer if there's a deal number and timer hasn't started yet
    if (dealNumber && !startTime && !timerAttemptedRef.current) {
      // Mark that we've tried to start the timer to avoid duplicate attempts
      timerAttemptedRef.current = true;
      
      // Create a new Date instance
      const newStartTime = new Date();
      
      console.log('Starting timer at:', newStartTime);
      
      // Use a timeout to give the DOM a chance to finish processing the blur event
      setTimeout(() => {
        // Store the actual Date object in state
        setFormState(prev => ({
          ...prev,
          startTime: newStartTime
        }));
      }, 50);
    }
  };
  
  // Reset the timer attempt flag when the deal number changes
  useEffect(() => {
    if (!dealNumber) {
      timerAttemptedRef.current = false;
    }
  }, [dealNumber]);
  
  // Style for form controls
  const inputSx = { 
    '& .MuiInputBase-root': {
      height: '36px',
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.85rem',
      transform: 'translate(14px, 10px) scale(1)'
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)'
    }
  };
  
  // Force rebuild of component
  console.log('FormHeader rendering with updated layout:', new Date().toISOString());
  
  // Smaller style for form controls with reduced height and font size
  const inputSxSmaller = { 
    '& .MuiInputBase-root': {
      height: '32px',
    },
    '& .MuiInputBase-input': {
      fontSize: '0.8rem',
      padding: '7px 14px',
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.75rem',
      transform: 'translate(14px, 8px) scale(1)'
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)'
    },
    '& .MuiFormHelperText-root': {
      fontSize: '0.7rem',
      marginTop: 0
    }
  };

  return (
    <>
      {/* Single consolidated card with all fields in one row - UPDATED LAYOUT */}
      <Card sx={{ mb: 1, backgroundColor: 'rgba(220, 230, 240, 0.7)', border: '1px solid #c0c0ff' }}>
        <CardContent sx={{ py: { xs: 0.5, sm: 0.5 }, "&:last-child": { pb: { xs: 0.5, sm: 0.5 } } }}>
          <Grid container spacing={1} alignItems="center">
            {/* Deal Number */}
            <Grid item xs={12} sm={3} md={1.5}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <NumbersIcon sx={{ color: 'primary.main', mr: 0.25, fontSize: '1rem' }} />
                <HookFormTextField
                  fieldId="dealNumber"
                  name="dealNumber"
                  label="Deal Number"
                  value={dealNumber || ''}
                  onChange={(value) => {
                    setFormState(prev => ({
                      ...prev,
                      dealNumber: value
                    }));
                  }}
                  onBlur={() => {
                    startTimer();
                  }}
                  required
                  sizeVariant="small"
                  error={submitError && !dealNumber}
                  helperText={submitError && !dealNumber ? 'Required' : ''}
                  autoComplete="off"
                  sx={inputSxSmaller}
                  fullWidth
                />
              </Box>
            </Grid>
            
            {/* Deal Type */}
            <Grid item xs={12} sm={3} md={1.6}>
              <FormControl fullWidth size="small" error={submitError && !dealType} sx={inputSxSmaller}>
                <InputLabel id="deal-type-label">Deal Type</InputLabel>
                <Select
                  labelId="deal-type-label"
                  value={dealType}
                  onChange={handleDealTypeChange}
                  label="Deal Type"
                  sx={{ fontSize: '0.8rem' }}
                >
                  <MenuItem value="Purchase" sx={{ fontSize: '0.8rem' }}>Purchase</MenuItem>
                  <MenuItem value="Refinance" sx={{ fontSize: '0.8rem' }}>Refinance</MenuItem>
                  <MenuItem value="Switch" sx={{ fontSize: '0.8rem' }}>Switch</MenuItem>
                  <MenuItem value="New Build" sx={{ fontSize: '0.8rem' }}>New Build</MenuItem>
                  <MenuItem value="Pre-Approval" sx={{ fontSize: '0.8rem' }}>Pre-Approval</MenuItem>
                  <MenuItem value="Secured Line of Credit" sx={{ fontSize: '0.8rem' }}>Secured LOC</MenuItem>
                  <MenuItem value="Business For Self" sx={{ fontSize: '0.8rem' }}>BFS</MenuItem>
                  <MenuItem value="Default Insurance" sx={{ fontSize: '0.8rem' }}>Default Insurance</MenuItem>
                  <MenuItem value="Bridge" sx={{ fontSize: '0.8rem' }}>Bridge</MenuItem>
                </Select>
                {submitError && !dealType && <FormHelperText>Required</FormHelperText>}
              </FormControl>
            </Grid>
            
            {/* Closing Date */}
            <Grid item xs={12} sm={3} md={1.5}>
              <HookFormTextField
                fieldId="closingDate"
                name="closingDate"
                label="Closing Date"
                type="date"
                value={closingDate || ''}
                onChange={(value) => {
                  setFormState(prev => ({ ...prev, closingDate: value }));
                }}
                required
                sizeVariant="small"
                InputLabelProps={{ shrink: true }}
                error={submitError && !closingDate}
                helperText={submitError && !closingDate ? 'Required' : ''}
                autoComplete="off"
                sx={inputSxSmaller}
                fullWidth
              />
            </Grid>
            
            {/* COF */}
            <Grid item xs={12} sm={3} md={1.5}>
              <HookFormTextField
                fieldId="cof"
                name="cof"
                label="COF"
                type="date"
                value={cof || ''}
                onChange={(value) => {
                  setFormState(prev => ({ ...prev, cof: value }));
                }}
                sizeVariant="small"
                InputLabelProps={{ shrink: true }}
                autoComplete="off"
                sx={inputSxSmaller}
                fullWidth
              />
            </Grid>

            {/* BFS Stage */}
            <Grid item xs={12} sm={3} md={1.8}>
              <FormControl fullWidth size="small" sx={inputSxSmaller}>
                <InputLabel id="bfs-stage-label">BFS Stage</InputLabel>
                <Select
                  labelId="bfs-stage-label"
                  value={bfsStage}
                  onChange={handleBfsStageChange}
                  label="BFS Stage"
                  sx={{ fontSize: '0.8rem' }}
                >
                  <MenuItem value="Bnk Stmt Reviewed" sx={{ fontSize: '0.8rem' }}>Bnk Stmt Reviewed</MenuItem>
                  <MenuItem value="Awaiting response" sx={{ fontSize: '0.8rem' }}>Awaiting response</MenuItem>
                  <MenuItem value="Awaiting Docs" sx={{ fontSize: '0.8rem' }}>Awaiting Docs</MenuItem>
                  <MenuItem value="Pending SDL" sx={{ fontSize: '0.8rem' }}>Pending SDL</MenuItem>
                  <MenuItem value="Pending review" sx={{ fontSize: '0.8rem' }}>Pending review</MenuItem>
                  <MenuItem value="N/A" sx={{ fontSize: '0.8rem' }}>N/A</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Form Started */}
            <Grid item xs={12} sm={3} md={2}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AccessTimeIcon sx={{ color: 'primary.main', mr: 0.25, fontSize: '0.9rem' }} />
                <Box>
                  <Typography variant="caption" color="textSecondary" sx={{ lineHeight: 1, fontSize: '0.7rem' }}>
                    Form Started
                  </Typography>
                  <Typography variant="body2" fontSize="0.75rem" noWrap>
                    {startTime ? startTime.toLocaleString() : 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            {/* Elapsed Time */}
            <Grid item xs={12} sm={3} md={2.1}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AccessTimeIcon sx={{ color: 'primary.main', mr: 0.25, fontSize: '0.9rem' }} />
                <Box>
                  <Typography variant="caption" color="textSecondary" sx={{ lineHeight: 1, fontSize: '0.7rem' }}>
                    Elapsed Time
                  </Typography>
                  <TimerDisplay 
                    startTime={startTime} 
                    onTimeUpdate={handleTimeUpdate}
                    fontSize="0.75rem" 
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  );
};

export default FormHeader; 